import { useMemo } from 'react';
import { isValid } from 'date-fns';
import DateInput from '@/components/ui/Form/DateInput';
import Text from '@/components/cms/Text';
import {
  ComplianceFieldErrors,
  ComplianceFieldNames
} from '@/components/ui/CreditTracker/util/constants';
import Icon from '@/components/ui/Icon';
import classnames from 'classnames';
import Shimmer from '@/components/ui/Shimmer';

interface DateIntervalPickerProps {
  handleStartDate: (startDate: string) => void;
  handleEndDate: (endDate: string) => void;
  startDate?: string;
  endDate?: string;
  title?: string;
  fieldsWithErrors?: ComplianceFieldErrors;
  setFieldsWithErrors: (errors: ComplianceFieldErrors) => void;
  isUpdating?: boolean;
  containerClassName?: string;
}

const DateIntervalPicker: React.FC<DateIntervalPickerProps> = ({
  handleStartDate,
  handleEndDate,
  startDate,
  endDate,
  title,
  fieldsWithErrors,
  isUpdating,
  setFieldsWithErrors,
  containerClassName
}: DateIntervalPickerProps) => {
  const shimmer = useMemo(() => {
    return (
      <div className={containerClassName}>
        <Shimmer className="mb-4 h-4 w-52" />
        <div className="flex flex-row flex-wrap items-center justify-between sm:flex-nowrap">
          <Shimmer className="mb-8 h-[4.375rem] w-full sm:mb-0" />
          <Shimmer className="mx-3 hidden items-center sm:block sm:h-2 sm:w-12" />
          <Shimmer className="mb-6 h-[4.375rem] w-full sm:mb-0" />
        </div>
      </div>
    );
  }, [containerClassName]);

  const initialStartDatePickerDate = useMemo(() => {
    const _start = new Date(startDate ?? '');
    if (isValid(_start)) return _start;

    const _end = new Date(endDate ?? '');

    if (isValid(_end)) return _end;
  }, [endDate, startDate]);

  const initialEndDatePickerDate = useMemo(() => {
    const _end = new Date(endDate ?? '');
    if (isValid(_end)) return _end;

    const _start = new Date(startDate ?? '');

    if (isValid(_start)) return _start;
  }, [endDate, startDate]);

  return isUpdating ? (
    shimmer
  ) : (
    <div className={containerClassName} data-component="DateIntervalPicker">
      <Text element="span" propertyName="Heading" className="text-2 mb-4">
        {title}
      </Text>
      <div
        className={classnames('flex flex-col gap-y-6 sm:max-h-[70px] sm:flex-row sm:gap-y-0', {
          'custom-error-message':
            !!fieldsWithErrors?.[ComplianceFieldNames.DATE_INTERVAL]?.showError
        })}
      >
        <DateInput
          label="Start date"
          className="mb-3 sm:mb-0"
          initialPickerDate={initialStartDatePickerDate}
          onChange={value => {
            handleStartDate(value);
            setFieldsWithErrors({
              ...fieldsWithErrors,
              [ComplianceFieldNames.START_DATE]: undefined,
              [ComplianceFieldNames.DATE_INTERVAL]: undefined
            });
          }}
          value={startDate}
          id={ComplianceFieldNames.START_DATE}
          name={ComplianceFieldNames.START_DATE}
          datePickerClassName="z-modal"
          required
          inheritedErrors={
            fieldsWithErrors && [
              {
                message: fieldsWithErrors?.[ComplianceFieldNames.START_DATE]?.message,
                match: fieldsWithErrors?.[ComplianceFieldNames.START_DATE]?.match,
                forceMatch: fieldsWithErrors?.[ComplianceFieldNames.START_DATE]?.forceMatch
              }
            ]
          }
          inputContainerClassName={
            fieldsWithErrors?.[ComplianceFieldNames.DATE_INTERVAL]?.showError
              ? 'border-2 border-red'
              : ''
          }
        />
        <div className="hidden items-center px-4 sm:flex">-</div>
        <DateInput
          label="End date"
          initialPickerDate={initialEndDatePickerDate}
          onChange={value => {
            handleEndDate(value);
            setFieldsWithErrors({
              ...fieldsWithErrors,
              [ComplianceFieldNames.END_DATE]: undefined,
              [ComplianceFieldNames.DATE_INTERVAL]: undefined
            });
          }}
          value={endDate}
          id={ComplianceFieldNames.END_DATE}
          name={ComplianceFieldNames.END_DATE}
          datePickerClassName="z-modal"
          required
          inheritedErrors={
            fieldsWithErrors && [
              {
                message: fieldsWithErrors?.[ComplianceFieldNames.END_DATE]?.message,
                match: fieldsWithErrors?.[ComplianceFieldNames.END_DATE]?.match,
                forceMatch: fieldsWithErrors?.[ComplianceFieldNames.END_DATE]?.forceMatch
              }
            ]
          }
          inputContainerClassName={
            fieldsWithErrors?.[ComplianceFieldNames.DATE_INTERVAL]?.showError
              ? 'border-2 border-red'
              : ''
          }
        />
      </div>
      <div>
        {!fieldsWithErrors?.[ComplianceFieldNames.START_DATE]?.showError &&
          !fieldsWithErrors?.[ComplianceFieldNames.END_DATE]?.showError &&
          fieldsWithErrors?.[ComplianceFieldNames.DATE_INTERVAL]?.showError && (
            <div className="label flex items-start gap-1 text-red">
              <Icon name="alert" size="small" />
              {fieldsWithErrors?.[ComplianceFieldNames.DATE_INTERVAL]?.message}
            </div>
          )}
      </div>
    </div>
  );
};

export default DateIntervalPicker;
